import { notification } from "antd";
import { get, isNil } from "lodash";
import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import format from "string-template";
import Header from "shared/components/Header";
import ProcessStepper from "shared/components/ProcessStepper";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { DATE_FORMAT } from "utils/constants";
import { STEP_INDEXES } from "../constants";
import View from "./View";

const Summary = ({ project, onBack }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const TRANSLATION = t("DETAIL.TEMPLATES.ACORIS_COLLECTIVE", {
    returnObjects: true,
  });

  const fields = get(project, "fields", {});
  const company = get(fields, "company", {});
  const selectedPlan = get(fields, "selectedPlan");
  const { data, loading, error } = useQuery(GENERAL_COSTS, {
    variables: {
      where: {
        project: { id: project.id },
        insurancePlan: { id: selectedPlan },
      },
      take: 1,
    },
    skip: isNil(selectedPlan),
    fetchPolicy: "network-only",
  });
  const cost = get(data, "generalCosts.data.0");

  const [updateProject, { loading: saving }] = useMutation(
    graphql.mutations.UPDATE_PROJECT,
    {
      refetchQueries: [
        {
          query: graphql.queries.PROJECT,
          awaitRefetchQueries: true,
          variables: { where: { id: project.id } },
        },
      ],
    },
  );

  const [proposal] = useState(() => ({
    recipient: get(company, "email", ""),
    subject: format(TRANSLATION.SUMMARY.PROPOSAL.DEFAULT_SUBJECT, {
      legalName: get(company, "legalName", ""),
    }),
    body: format(TRANSLATION.SUMMARY.PROPOSAL.DEFAULT_BODY, {
      legalName: get(company, "legalName", ""),
      effectiveDate: moment(get(fields, "effectiveDate")).format(DATE_FORMAT),
    }),
    ...get(fields, "proposal", {}),
  }));

  const onSaveProposal = async (values) => {
    await updateProject({
      variables: {
        where: { id: project.id },
        data: {
          fields: {
            ...fields,
            proposal: { ...values, savedAt: moment().toISOString() },
          },
        },
      },
    });
    setDrawerOpen(false);
    notification.success({
      message: TRANSLATION.SUMMARY.PROPOSAL.SAVED,
    });
  };

  const steps = useMemo(
    () => Object.values(TRANSLATION.STEPS).map((title) => ({ title })),
    [TRANSLATION],
  );

  return (
    <>
      <Header
        key="header"
        title={TRANSLATION.SUMMARY.TITLE}
        subtitle={TRANSLATION.SUMMARY.SUBTITLE}
        actions={[]}
      />
      <ProcessStepper
        steps={steps}
        current={STEP_INDEXES.SUMMARY}
        aria-label={TRANSLATION.STEPPER_LABEL}
      />
      <View
        translation={TRANSLATION}
        project={project}
        costDetails={get(cost, "costDetails")}
        costLoading={loading}
        costFailed={!isNil(error) || isNil(selectedPlan)}
        proposal={proposal}
        drawerOpen={drawerOpen}
        onOpenDrawer={() => setDrawerOpen(true)}
        onCloseDrawer={() => setDrawerOpen(false)}
        onSaveProposal={onSaveProposal}
        saving={saving}
        onBack={onBack}
      />
    </>
  );
};

export default Summary;
